import type { ComponentChildren } from "preact";
import { useEffect, useState } from "preact/hooks";
import { createApi, type Api } from "../api.js";
import { dm, dmSv, when } from "../format.js";
import { texts, useLang, type Lang } from "../i18n.js";
import { navigate } from "../router.js";
import { ProcessBar } from "../process.js";
import { useHashFocus } from "./projectActivity.js";
import {
  Callout,
  ConfirmDialog,
  Dialog,
  ErrorBox,
  Loading,
  MoreOptions,
  Pill,
  RowMenu,
  toast,
} from "../ui.js";
import {
  firstShoot,
  kindLabels,
  PublicFrame,
  ShootDateField,
  publicError,
  talentLine,
  usePublicLang,
  type Kind,
  type ShootDay,
} from "./Actors.js";

/**
 * Casting (owner 2026-10-18): a project's roles and the people considered for each – from the library, or someone new
 * who then joins the library. The producer asks the candidates (emailed from hub@, or copied), each answers on a personal
 * link whether they can, with a note and a self-tape link, and the one chosen is booked onto Actors, where the model
 * release is signed. "Cast…" in the library sends the people picked to a role in one go.
 */

type Stage = "shortlist" | "asked" | "yes" | "no" | "booked" | "passed";

/** A booked person's release, drawn by the Actors tab: its status, what it says and the one next step. */
export interface BookedSlot {
  status: string;
  tone: "ok" | "info" | "warn" | "neutral";
  main: ComponentChildren;
  actions: ComponentChildren;
}
interface Candidate {
  id: string;
  roleId: string;
  talentId: string;
  name: string;
  email: string | null;
  phone: string | null;
  kinds: Kind[];
  ageRange: string | null;
  locations: string[];
  languages: string[];
  rating: number | null;
  stage: Stage;
  note: string;
  castingId: string | null;
  release: "draft" | "sent" | "signed" | "cancelled" | null;
  birthDate: string | null;
  guardianName?: string | null;
  guardianEmail?: string | null;
  askedAt: string | null;
  answeredAt: string | null;
  answerNote: string | null;
  tapeUrl: string | null;
  byEmail: boolean;
}
interface Role {
  id: string;
  name: string;
  kind: Kind;
  brief: string;
  shootDate: string | null;
  fee: number | null;
  answerBy: string | null;
  candidates: Candidate[];
  booked: number;
}
interface CastingView {
  roles: Role[];
  mailer: boolean;
  /** the project's shoot days and the limits on use written last (owner 2026-10-27) */
  shoots?: ShootDay[];
  usage?: string | null;
}
interface AskResult {
  sent: {
    candidateId: string;
    name: string;
    emailed: boolean;
    url: string;
    email: { to: string | null; subject: string; text: string };
  }[];
  noEmail: string[];
  notEmailed: string | null;
  role: Role;
}
interface LibraryPerson {
  id: string;
  name: string;
  email: string | null;
  kinds: Kind[];
  ageRange: string | null;
  locations: string[];
  languages: string[];
}

const TX = texts(
  {
    casting: "Roles and actors",
    progress: ["Not asked yet", "Asked", "Said yes", "Booked", "Release out", "Signed"],
    shootWarn: (d: string, n: number, of: number) =>
      `Shoot ${d}: ${n} of ${of} ${of === 1 ? "role has" : "roles have"} no signed model release.`,
    nSigned: (n: number, of: number) => `${n} of ${of} signed`,
    addDirect: "Add someone directly…",
    askOne: "Ask",
    castingInfo:
      "Roles to fill and the people you consider. Ask them by email and see who can. Book the one you choose with the fee you agreed – the model release goes to them to sign, and signed they are on the project.",
    newRole: "New role",
    steps: [
      "Add a role – the shoot day comes from Shoots",
      "Add people from the library and ask who can",
      "Book the one you choose and sign the model release, here on site or by email",
    ],
    firstRole: "Add the first role",
    noRoles:
      "No roles yet. Add one, or pick people in the library under Actors and press Cast…",
    stage: {
      shortlist: "Candidate",
      asked: "Asked",
      yes: "Can",
      no: "Can't",
      booked: "Booked",
      passed: "Not chosen",
    } as Record<Stage, string>,
    shoot: (d: string) => `Shoot ${d}`,
    answerBy: (d: string) => `answer by ${d}`,
    kr: (n: number) => `${n.toLocaleString("sv-SE")} kr`,
    nCandidates: (n: number, booked: number) =>
      `${n} ${n === 1 ? "candidate" : "candidates"}${booked ? ` · ${booked} booked` : ""}`,
    noCandidates: "No candidates yet.",
    addPeople: "Add people",
    askPicked: (n: number) => (n ? `Ask ${n} picked` : "Ask…"),
    pickToAsk: "Pick who to ask",
    book: "Book",
    roleMenu: (name: string) => `More for the role ${name}`,
    rowMenu: (name: string) => `More for ${name}`,
    editRole: "Edit role",
    removeRole: "Remove role",
    markYes: "Mark as can",
    markNo: "Mark as can't",
    markPassed: "Not chosen",
    backToCandidate: "Back to candidate",
    removeCandidate: "Take off the role",
    askedOn: (w: string) => `Asked ${w}`,
    answered: (w: string) => `answered ${w}`,
    selfTape: "Self-tape",
    noEmail: "No email – call or text them",
    booked: (name: string) =>
      `${name} is booked – the model release is under Actors below`,
    release: {
      draft: "Booked · release not sent",
      sent: "Release sent",
      signed: "Signed",
      cancelled: "Cancelled",
    } as Record<"draft" | "sent" | "signed" | "cancelled", string>,
    bookTitle: (name: string, role: string) => `Book ${name} as ${role}`,
    bookBody:
      "Check what you agreed – it starts from the role and the project. The model release is made from it; next you choose to sign it here or email it.",
    agreedFee: "Agreed fee (kr excl. VAT)",
    feeEmpty: "Leave empty if unpaid",
    under18: "Under 18?",
    byBirthYes: "Yes – by the birth date, on the shoot date.",
    byBirthNo: "No – by the birth date.",
    guardianName: "Parent or guardian's name",
    guardianEmail: "Their email",
    guardianHint: "A child can't sign; a parent or guardian signs for them.",
    bookSend: "Book",
    usage: "Limits on use",
    usageHint:
      "Only if the material may not be used everywhere, e.g. “Web and social media for 12 months”. Empty: all media, no time limit.",
    usageFromProject:
      "As for the others on the project – change it if this person agreed something else.",
    theGuardian: "the parent or guardian",
    howSign: (who: string) => `The model release is ready. How does ${who} sign?`,
    signOnsite: "Sign on site now",
    signOnsiteHint: (who: string) =>
      `${who} signs on this screen, with a finger or the mouse. No code needed.`,
    sendSigning: "Email it for signing",
    sendSigningHint: (who: string) =>
      `${who} gets a personal link and confirms with a code to their email.`,
    opening: "Opening…",
    preview: "Preview the model release",
    later: "Later",
    booking: "Booking…",
    bookedTitle: (name: string) => `${name} is booked`,
    sendRelease: "Send the release to sign",
    guardianOption: "A parent or guardian signs (under 18)",
    mailedTo: (to: string) =>
      `The model release is emailed to ${to} for signing. You hear when they open and sign it.`,
    linkBody: (to: string | null) =>
      `The Hub could not email ${to ?? "them"} (on staging only @klarafardigafilm.se gets mail). Copy the link and send it yourself. It works for 30 days.`,
    linkLater: (why: string) =>
      `${why} Send it for signing under Actors below.`,
    removeRoleTitle: (name: string) => `Remove the role ${name}?`,
    removeRoleBody:
      "The role and its candidates go. Anyone already booked stays under Actors, and the answer links stop working.",
    // role dialog
    roleTitleNew: "New role",
    roleTitleEdit: (name: string) => `Edit ${name}`,
    roleName: "Role",
    roleNameHint: "e.g. Mother, 35–45 · or Narrator",
    type: "Type",
    shootDate: "Shoot or recording date",
    fee: "Fee offered (kr excl. VAT)",
    feeHint: "Shown in the request",
    answerByLabel: "Answer by",
    brief: "What we look for",
    briefHint: "Age, look, language or accent – shown in the request",
    cancel: "Cancel",
    save: "Save",
    saving: "Saving…",
    create: "Add role",
    // add people
    addTitle: (role: string) => `Add people to ${role}`,
    fromLibrary: "From the library",
    someoneNew: "Someone new",
    search: "Search the library",
    searchPh: "Name, city, language…",
    noMatch: "No one matches. Add them as someone new.",
    onRole: "already on the role",
    nPicked: (n: number) => `${n} picked`,
    add: "Add",
    adding: "Adding…",
    name: "Name",
    email: "Email",
    phone: "Phone",
    newHint: "They are added to the library too.",
    addedN: (n: number, already: string[]) =>
      `${n} added${already.length ? ` · already there: ${already.join(", ")}` : ""}`,
    // ask
    askTitle: (role: string) => `Ask about ${role}`,
    askBody: (n: number, mail: boolean) =>
      `${n} ${n === 1 ? "person gets" : "people get"} a personal link where they answer whether they can, with a note and a self-tape link. ${mail ? "The Hub emails them from hub@ – replies come to you." : "Copy the email text to each person afterwards."}`,
    askNoEmail: (names: string[]) =>
      `No email for ${names.join(", ")} – copy their link and text it.`,
    message: "Your message (optional)",
    messageHint: "e.g. where the shoot is, or what to send in the self-tape",
    send: "Send",
    sending: "Sending…",
    askResult: (emailed: number, total: number) =>
      emailed === total
        ? `Sent to ${total}`
        : `${emailed} of ${total} emailed – copy the rest below`,
    emailed: "Emailed",
    copyText: "Copy email text",
    copyLink: "Copy link",
    copied: "Copied",
    copyFailed: "Could not copy – select the text and copy it by hand",
    done: "Done",
    // cast from the library
    castTitle: (n: number) => `Cast ${n} ${n === 1 ? "person" : "people"}`,
    project: "Project",
    pickProject: "Choose a project",
    mine: "My projects",
    others: "Other projects",
    role: "Role",
    newRoleOpt: "New role…",
    loadingRoles: "Loading the roles…",
    cast: "Cast",
    casting2: "Casting…",
    castDone: (n: number, role: string, project: string) =>
      `${n} cast as ${role} on ${project}`,
    open: "Open",
    cast1: "Cast…",
    picked: (n: number) => `${n} picked`,
    clear: "Clear",
  },
  {
    casting: "Roller och skådespelare",
    progress: ["Inte tillfrågad", "Tillfrågad", "Svarat ja", "Bokad", "Avtal ute", "Signerat"],
    shootWarn: (d: string, n: number, of: number) =>
      `Inspelning ${d}: ${n} av ${of} ${of === 1 ? "roll" : "roller"} har inte signerat modellavtal.`,
    nSigned: (n: number, of: number) => `${n} av ${of} signerade`,
    addDirect: "Lägg till någon direkt…",
    askOne: "Fråga",
    castingInfo:
      "Roller att tillsätta och de personer ni funderar på. Fråga dem via mejl och se vem som kan. Boka den ni väljer med arvodet ni kommit överens om – modellavtalet skickas för signering, och när det är signerat är personen med i projektet.",
    newRole: "Ny roll",
    steps: [
      "Lägg till en roll – inspelningsdagen hämtas från Inspelningar",
      "Lägg till personer från biblioteket och fråga vem som kan",
      "Boka den ni väljer och signera modellavtalet, här på plats eller via mejl",
    ],
    firstRole: "Lägg till första rollen",
    noRoles:
      "Inga roller än. Lägg till en, eller bocka för personer i biblioteket under Skådespelare och tryck Casta…",
    stage: {
      shortlist: "Kandidat",
      asked: "Tillfrågad",
      yes: "Kan",
      no: "Kan inte",
      booked: "Bokad",
      passed: "Inte vald",
    },
    shoot: (d: string) => `Inspelning ${d}`,
    answerBy: (d: string) => `svar senast ${d}`,
    kr: (n: number) => `${n.toLocaleString("sv-SE")} kr`,
    nCandidates: (n: number, booked: number) =>
      `${n} ${n === 1 ? "kandidat" : "kandidater"}${booked ? ` · ${booked} bokad${booked === 1 ? "" : "e"}` : ""}`,
    noCandidates: "Inga kandidater än.",
    addPeople: "Lägg till personer",
    askPicked: (n: number) => (n ? `Fråga ${n} valda` : "Fråga…"),
    pickToAsk: "Välj vilka du vill fråga",
    book: "Boka",
    roleMenu: (name: string) => `Mer för rollen ${name}`,
    rowMenu: (name: string) => `Mer för ${name}`,
    editRole: "Ändra roll",
    removeRole: "Ta bort roll",
    markYes: "Markera som kan",
    markNo: "Markera som kan inte",
    markPassed: "Inte vald",
    backToCandidate: "Tillbaka till kandidat",
    removeCandidate: "Ta bort från rollen",
    askedOn: (w: string) => `Tillfrågad ${w}`,
    answered: (w: string) => `svarade ${w}`,
    selfTape: "Self-tape",
    noEmail: "Ingen e-post – ring eller sms:a",
    booked: (name: string) =>
      `${name} är bokad – modellavtalet finns under Skådespelare nedanför`,
    release: {
      draft: "Bokad · avtal ej skickat",
      sent: "Avtal skickat",
      signed: "Signerat",
      cancelled: "Avbrutet",
    },
    bookTitle: (name: string, role: string) => `Boka ${name} som ${role}`,
    bookBody:
      "Kontrollera det ni kommit överens om – det är ifyllt från rollen och projektet. Modellavtalet görs av det, och sedan väljer du att signera här eller mejla det.",
    agreedFee: "Överenskommet arvode (kr exkl. moms)",
    feeEmpty: "Lämna tomt om det är oavlönat",
    under18: "Under 18?",
    byBirthYes: "Ja – enligt födelsedatumet, på inspelningsdagen.",
    byBirthNo: "Nej – enligt födelsedatumet.",
    guardianName: "Förälderns eller vårdnadshavarens namn",
    guardianEmail: "Deras e-post",
    guardianHint:
      "Ett barn kan inte signera själv; en förälder eller vårdnadshavare signerar.",
    bookSend: "Boka",
    usage: "Begränsning av användning",
    usageHint:
      "Bara om materialet inte får användas överallt, t.ex. ”Webb och sociala medier i 12 månader”. Tomt: alla medier, utan tidsgräns.",
    usageFromProject:
      "Samma som för de andra i projektet – ändra om ni kommit överens om något annat.",
    theGuardian: "vårdnadshavaren",
    howSign: (who: string) => `Modellavtalet är klart. Hur ska ${who} signera?`,
    signOnsite: "Signera på plats nu",
    signOnsiteHint: (who: string) =>
      `${who} signerar på den här skärmen, med fingret eller musen. Ingen kod behövs.`,
    sendSigning: "Mejla för signering",
    sendSigningHint: (who: string) =>
      `${who} får en personlig länk och bekräftar med en kod till sin e-post.`,
    opening: "Öppnar…",
    preview: "Förhandsvisa modellavtalet",
    later: "Senare",
    booking: "Bokar…",
    bookedTitle: (name: string) => `${name} är bokad`,
    sendRelease: "Skicka avtalet för signering",
    guardianOption: "En förälder eller vårdnadshavare signerar (under 18)",
    mailedTo: (to: string) =>
      `Modellavtalet är mejlat till ${to} för signering. Du får en notis när personen öppnar och signerar.`,
    linkBody: (to: string | null) =>
      `Hubben kunde inte mejla ${to ?? "personen"} (på staging får bara @klarafardigafilm.se mejl). Kopiera länken och skicka den själv. Den gäller i 30 dagar.`,
    linkLater: (why: string) =>
      `${why} Skicka den för signering under Skådespelare nedanför.`,
    removeRoleTitle: (name: string) => `Ta bort rollen ${name}?`,
    removeRoleBody:
      "Rollen och dess kandidater försvinner. Den som redan är bokad finns kvar under Skådespelare, och svarslänkarna slutar fungera.",
    roleTitleNew: "Ny roll",
    roleTitleEdit: (name: string) => `Ändra ${name}`,
    roleName: "Roll",
    roleNameHint: "t.ex. Mamma, 35–45 · eller Speaker",
    type: "Typ",
    shootDate: "Inspelningsdatum",
    fee: "Arvode som erbjuds (kr exkl. moms)",
    feeHint: "Visas i förfrågan",
    answerByLabel: "Svar senast",
    brief: "Vad vi söker",
    briefHint: "Ålder, utseende, språk eller dialekt – visas i förfrågan",
    cancel: "Avbryt",
    save: "Spara",
    saving: "Sparar…",
    create: "Lägg till roll",
    addTitle: (role: string) => `Lägg till personer i ${role}`,
    fromLibrary: "Från biblioteket",
    someoneNew: "Någon ny",
    search: "Sök i biblioteket",
    searchPh: "Namn, stad, språk…",
    noMatch: "Ingen matchar. Lägg till personen som ny.",
    onRole: "redan i rollen",
    nPicked: (n: number) => `${n} valda`,
    add: "Lägg till",
    adding: "Lägger till…",
    name: "Namn",
    email: "E-post",
    phone: "Telefon",
    newHint: "Personen läggs också till i biblioteket.",
    addedN: (n: number, already: string[]) =>
      `${n} tillagda${already.length ? ` · fanns redan: ${already.join(", ")}` : ""}`,
    askTitle: (role: string) => `Fråga om ${role}`,
    askBody: (n: number, mail: boolean) =>
      `${n} ${n === 1 ? "person får" : "personer får"} en personlig länk där de svarar om de kan, med en kommentar och en länk till en self-tape. ${mail ? "Hubben mejlar från hub@ – svar på mejlet kommer till dig." : "Kopiera mejltexten till var och en efteråt."}`,
    askNoEmail: (names: string[]) =>
      `Ingen e-post för ${names.join(", ")} – kopiera länken och sms:a den.`,
    message: "Ditt meddelande (valfritt)",
    messageHint:
      "t.ex. var inspelningen är, eller vad self-tapen ska innehålla",
    send: "Skicka",
    sending: "Skickar…",
    askResult: (emailed: number, total: number) =>
      emailed === total
        ? `Skickat till ${total}`
        : `${emailed} av ${total} mejlade – kopiera resten nedan`,
    emailed: "Mejlad",
    copyText: "Kopiera mejltext",
    copyLink: "Kopiera länk",
    copied: "Kopierat",
    copyFailed: "Kunde inte kopiera – markera texten och kopiera för hand",
    done: "Klar",
    castTitle: (n: number) => `Casta ${n} ${n === 1 ? "person" : "personer"}`,
    project: "Projekt",
    pickProject: "Välj ett projekt",
    mine: "Mina projekt",
    others: "Andra projekt",
    role: "Roll",
    newRoleOpt: "Ny roll…",
    loadingRoles: "Hämtar rollerna…",
    cast: "Casta",
    casting2: "Castar…",
    castDone: (n: number, role: string, project: string) =>
      `${n} castade som ${role} på ${project}`,
    open: "Öppna",
    cast1: "Casta…",
    picked: (n: number) => `${n} valda`,
    clear: "Avmarkera",
  },
);

const TONE: Record<Stage, "ok" | "info" | "warn" | "neutral" | "crit"> = {
  shortlist: "neutral",
  asked: "info",
  yes: "ok",
  no: "warn",
  booked: "ok",
  passed: "neutral",
};
const val = (e: Event) =>
  (e.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement)
    .value;
const day = (k: string, lang: Lang) => (lang === "sv" ? dmSv(k) : dm(k));

/**
 * How far a role has come on the way to a signed model release (owner 2026-10-28), in five steps like a project's
 * stages: 1 asked, 2 said yes, 3 booked, 4 release out for signing, 5 signed. The role's furthest candidate counts; a no
 * does not move it back.
 */
export function roleProgress(r: Pick<Role, "candidates">): number {
  const c = r.candidates;
  const booked = c.filter((x) => x.stage === "booked");
  if (booked.some((x) => x.release === "signed")) return 5;
  if (booked.some((x) => x.release === "sent")) return 4;
  if (booked.length) return 3;
  if (c.some((x) => x.stage === "yes")) return 2;
  if (c.some((x) => x.askedAt || x.stage === "asked" || x.stage === "no")) return 1;
  return 0;
}

/** Roles not signed with a shoot within a week: the warning on top of the casting (producer side only). */
export function shootWarning(roles: Pick<Role, "candidates" | "shootDate">[], today: string): { date: string; open: number; of: number } | null {
  const soon = roles.filter((r) => r.shootDate && r.shootDate >= today && Date.parse(r.shootDate) - Date.parse(today) <= 7 * 86400_000);
  if (!soon.length) return null;
  const date = soon.map((r) => r.shootDate!).sort()[0]!;
  const that = soon.filter((r) => r.shootDate === date);
  const open = that.filter((r) => roleProgress(r) < 5).length;
  return open ? { date, open, of: that.length } : null;
}

async function copy(text: string, ok: string, failed: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast({ text: ok });
  } catch {
    toast({ text: failed });
  }
}

function roleLine(
  r: Pick<Role, "kind" | "shootDate" | "fee" | "answerBy">,
  lang: Lang,
) {
  const t = TX[lang];
  return [
    kindLabels(lang)[r.kind],
    r.shootDate ? t.shoot(day(r.shootDate, lang)) : null,
    r.fee ? t.kr(r.fee) : null,
    r.answerBy ? t.answerBy(day(r.answerBy, lang)) : null,
  ]
    .filter(Boolean)
    .join(" · ");
}

/* ---------------- a role: new or edit ---------------- */

interface RoleForm {
  name: string;
  kind: Kind;
  shootDate: string;
  fee: string;
  answerBy: string;
  brief: string;
}
const roleForm = (r: Role | null, kind: Kind = "actor"): RoleForm => ({
  name: r?.name ?? "",
  kind: r?.kind ?? kind,
  shootDate: r?.shootDate ?? "",
  fee: r?.fee === null || r?.fee === undefined ? "" : String(r.fee),
  answerBy: r?.answerBy ?? "",
  brief: r?.brief ?? "",
});

function RoleFields(p: {
  f: RoleForm;
  set: (f: RoleForm) => void;
  more: string;
  shoots?: ShootDay[];
}) {
  const lang = useLang();
  const t = TX[lang];
  const kinds = kindLabels(lang);
  const { f } = p;
  const field = (k: keyof RoleForm) => (e: Event) =>
    p.set({ ...f, [k]: val(e) });
  return (
    <>
      <label class="field">
        <span class="label">{t.roleName}</span>
        <span class="hint">{t.roleNameHint}</span>
        <input
          class="input input--wide"
          value={f.name}
          maxLength={200}
          onInput={field("name")}
        />
      </label>
      <div class="field">
        <span class="label">{t.type}</span>
        <div class="seg seg--sm" role="group" aria-label={t.type}>
          {(["actor", "voice"] as const).map((k) => (
            <button
              key={k}
              type="button"
              class={`seg-btn${f.kind === k ? " is-on" : ""}`}
              aria-pressed={f.kind === k}
              onClick={() => p.set({ ...f, kind: k })}
            >
              {kinds[k]}
            </button>
          ))}
        </div>
      </div>
      <ShootDateField
        label={t.shootDate}
        value={f.shootDate}
        onChange={(v) => p.set({ ...f, shootDate: v })}
        shoots={p.shoots ?? []}
      />
      <MoreOptions
        id={p.more}
        filled={[f.fee, f.answerBy, f.brief].filter(Boolean).length}
      >
        <div class="act-grid">
          <label class="field">
            <span class="label">{t.fee}</span>
            <span class="hint">{t.feeHint}</span>
            <input
              class="input"
              inputMode="numeric"
              value={f.fee}
              onInput={field("fee")}
            />
          </label>
          <label class="field">
            <span class="label">{t.answerByLabel}</span>
            <input
              class="input"
              type="date"
              value={f.answerBy}
              onInput={field("answerBy")}
            />
          </label>
        </div>
        <label class="field">
          <span class="label">{t.brief}</span>
          <span class="hint">{t.briefHint}</span>
          <textarea
            class="textarea act-usage"
            value={f.brief}
            maxLength={2000}
            onInput={field("brief")}
          />
        </label>
      </MoreOptions>
    </>
  );
}

const roleBody = (f: RoleForm) => ({
  name: f.name,
  kind: f.kind,
  shootDate: f.shootDate || null,
  fee: f.fee || null,
  answerBy: f.answerBy || null,
  brief: f.brief,
});

function RoleDialog(p: {
  api: Api;
  id: string;
  edit: Role | null;
  shoots: ShootDay[];
  onDone: (r: Role) => void;
  onClose: () => void;
}) {
  const lang = useLang();
  const t = TX[lang];
  // a new role starts on the project's shoot day
  const [f, setF] = useState<RoleForm>(
    p.edit
      ? roleForm(p.edit)
      : { ...roleForm(null), shootDate: firstShoot(p.shoots) },
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = p.edit
        ? await p.api.put<{ role: Role }>(
            `/api/projects/${p.id}/casting/roles/${p.edit.id}`,
            roleBody(f),
          )
        : await p.api.post<{ role: Role }>(
            `/api/projects/${p.id}/casting/roles`,
            roleBody(f),
          );
      p.onDone(r.role);
    } catch (e) {
      setErr(e);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={p.edit ? t.roleTitleEdit(p.edit.name) : t.roleTitleNew}
      onClose={p.onClose}
      onSubmit={() => void save()}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            disabled={busy || !f.name.trim()}
          >
            {busy ? t.saving : p.edit ? t.save : t.create}
          </button>
        </>
      }
    >
      <div class="stack tal-form">
        <RoleFields
          f={f}
          set={setF}
          more="casting-role"
          shoots={p.shoots}
        />
        {err ? <ErrorBox error={err} /> : null}
      </div>
    </Dialog>
  );
}

/* ---------------- add people to a role ---------------- */

function AddPeopleDialog(p: {
  api: Api;
  id: string;
  role: Role;
  onDone: (r: Role) => void;
  onClose: () => void;
}) {
  const lang = useLang();
  const t = TX[lang];
  const [lib, setLib] = useState<LibraryPerson[] | null>(null);
  const [mode, setMode] = useState<"library" | "new">("library");
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [person, setPerson] = useState({ name: "", email: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => {
    p.api
      .get<{ talent: LibraryPerson[] }>("/api/talent")
      .then((r) => setLib(r.talent), setErr);
  }, []);
  const on = new Set(p.role.candidates.map((c) => c.talentId));
  const needle = q.trim().toLocaleLowerCase("sv");
  const shown = (lib ?? [])
    .filter(
      (x) =>
        !needle ||
        [x.name, x.email ?? "", ...x.locations, ...x.languages]
          .join(" ")
          .toLocaleLowerCase("sv")
          .includes(needle),
    )
    // the role's type first
    .sort(
      (a, b) =>
        Number(b.kinds.includes(p.role.kind)) -
          Number(a.kinds.includes(p.role.kind)) ||
        a.name.localeCompare(b.name, "sv"),
    );
  const flip = (id: string) =>
    setPicked(
      picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id],
    );
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await p.api.post<{
        added: number;
        already: string[];
        role: Role;
      }>(
        `/api/projects/${p.id}/casting/roles/${p.role.id}/candidates`,
        mode === "library"
          ? { talentIds: picked }
          : {
              talent: {
                name: person.name,
                email: person.email || null,
                phone: person.phone || null,
              },
            },
      );
      toast({ text: t.addedN(r.added, r.already) });
      p.onDone(r.role);
    } catch (e) {
      setErr(e);
      setBusy(false);
    }
  };
  const ready =
    mode === "library" ? picked.length > 0 : person.name.trim().length > 0;
  return (
    <Dialog
      title={t.addTitle(p.role.name)}
      onClose={p.onClose}
      onSubmit={() => void save()}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            disabled={busy || !ready}
          >
            {busy
              ? t.adding
              : mode === "library" && picked.length
                ? `${t.add} (${picked.length})`
                : t.add}
          </button>
        </>
      }
    >
      <div class="stack tal-form">
        <div class="seg seg--sm" role="group">
          {(["library", "new"] as const).map((m) => (
            <button
              key={m}
              type="button"
              class={`seg-btn${mode === m ? " is-on" : ""}`}
              aria-pressed={mode === m}
              onClick={() => setMode(m)}
            >
              {m === "library" ? t.fromLibrary : t.someoneNew}
            </button>
          ))}
        </div>
        {mode === "library" ? (
          !lib ? (
            <Loading />
          ) : (
            <>
              <label class="field">
                <span class="label">{t.search}</span>
                <input
                  class="input input--wide"
                  type="search"
                  value={q}
                  placeholder={t.searchPh}
                  onInput={(e) => setQ(val(e))}
                />
              </label>
              <div class="act-pick" role="group" aria-label={t.fromLibrary}>
                {shown.slice(0, 200).map((x) => (
                  <label
                    key={x.id}
                    class={`check${on.has(x.id) ? " is-disabled" : ""}`}
                  >
                    <input
                      type="checkbox"
                      disabled={on.has(x.id)}
                      checked={on.has(x.id) || picked.includes(x.id)}
                      onChange={() => flip(x.id)}
                    />
                    <span>
                      <strong>{x.name}</strong>{" "}
                      <span class="small muted">
                        {on.has(x.id) ? t.onRole : talentLine(x, lang)}
                      </span>
                    </span>
                  </label>
                ))}
                {!shown.length ? <p class="muted">{t.noMatch}</p> : null}
              </div>
              {picked.length ? (
                <p class="small muted">{t.nPicked(picked.length)}</p>
              ) : null}
            </>
          )
        ) : (
          <>
            <p class="small muted">{t.newHint}</p>
            <label class="field">
              <span class="label">{t.name}</span>
              <input
                class="input input--wide"
                value={person.name}
                autoComplete="off"
                onInput={(e) => setPerson({ ...person, name: val(e) })}
              />
            </label>
            <div class="act-grid">
              <label class="field">
                <span class="label">{t.email}</span>
                <input
                  class="input"
                  type="email"
                  value={person.email}
                  autoComplete="off"
                  onInput={(e) => setPerson({ ...person, email: val(e) })}
                />
              </label>
              <label class="field">
                <span class="label">{t.phone}</span>
                <input
                  class="input"
                  type="tel"
                  value={person.phone}
                  autoComplete="off"
                  onInput={(e) => setPerson({ ...person, phone: val(e) })}
                />
              </label>
            </div>
          </>
        )}
        {err ? <ErrorBox error={err} /> : null}
      </div>
    </Dialog>
  );
}

/* ---------------- ask the candidates ---------------- */

function AskDialog(p: {
  api: Api;
  id: string;
  role: Role;
  who: Candidate[];
  mailer: boolean;
  onDone: (r: Role) => void;
  onClose: () => void;
}) {
  const lang = useLang();
  const t = TX[lang];
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [res, setRes] = useState<AskResult | null>(null);
  const noEmail = p.who.filter((c) => !c.email).map((c) => c.name);
  const send = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await p.api.post<AskResult>(
        `/api/projects/${p.id}/casting/roles/${p.role.id}/ask`,
        { candidateIds: p.who.map((c) => c.id), message: message || null },
      );
      setRes(r);
      p.onDone(r.role);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };
  if (res) {
    const emailed = res.sent.filter((s) => s.emailed).length;
    return (
      <Dialog
        title={t.askTitle(p.role.name)}
        onClose={p.onClose}
        foot={
          <button type="button" class="btn btn-primary" onClick={p.onClose}>
            {t.done}
          </button>
        }
      >
        <div class="stack">
          <Callout
            tone={emailed === res.sent.length ? "ok" : "info"}
            title={t.askResult(emailed, res.sent.length)}
          >
            {res.notEmailed && emailed < res.sent.length
              ? res.notEmailed
              : null}
          </Callout>
          <ul class="act-list">
            {res.sent.map((s) => (
              <li key={s.candidateId} class="act-row">
                <div class="act-main">
                  <strong>{s.name}</strong>
                  <span class="small muted">
                    {s.emailed
                      ? `${t.emailed} · ${s.email.to}`
                      : (s.email.to ?? t.noEmail)}
                  </span>
                </div>
                {s.emailed ? (
                  <Pill tone="ok">{t.emailed}</Pill>
                ) : (
                  <div class="cluster act-actions">
                    <button
                      type="button"
                      class="btn btn-sm"
                      onClick={() => void copy(s.url, t.copied, t.copyFailed)}
                    >
                      {t.copyLink}
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-primary"
                      onClick={() =>
                        void copy(
                          `${s.email.subject}\n\n${s.email.text}`,
                          t.copied,
                          t.copyFailed,
                        )
                      }
                    >
                      {t.copyText}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </Dialog>
    );
  }
  return (
    <Dialog
      title={t.askTitle(p.role.name)}
      onClose={p.onClose}
      onSubmit={() => void send()}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button type="submit" class="btn btn-primary" disabled={busy}>
            {busy ? t.sending : t.send}
          </button>
        </>
      }
    >
      <div class="stack tal-form">
        <p>{t.askBody(p.who.length, p.mailer)}</p>
        <p class="small">
          <strong>{p.who.map((c) => c.name).join(", ")}</strong>
        </p>
        {noEmail.length ? (
          <p class="small act-warn">{t.askNoEmail(noEmail)}</p>
        ) : null}
        <label class="field">
          <span class="label">{t.message}</span>
          <span class="hint">{t.messageHint}</span>
          <textarea
            class="textarea act-usage"
            value={message}
            maxLength={1000}
            onInput={(e) => setMessage(val(e))}
          />
        </label>
        {err ? <ErrorBox error={err} /> : null}
      </div>
    </Dialog>
  );
}

/* ---------------- book: the agreed terms, then the model release to sign ---------------- */

const minorOn = (birth: string, on: string) =>
  /^\d{4}-\d{2}-\d{2}$/.test(birth) &&
  `${Number(birth.slice(0, 4)) + 18}${birth.slice(4)}` > on;
const todayKey = () =>
  new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Stockholm" });

interface BookResult {
  role: Role;
  castingId: string;
  /** the release went out for e-signing: mailed, or the link to copy */
  signing: { to: string; mailed: boolean; url: string | null } | null;
  linkError: string | null;
}

interface SendOut {
  request: { parties: { role: string; email: string }[] } | null;
  links: { url: string; email: string | null }[];
  onsite?: { token: string; url: string; session: string };
}

/**
 * Book (owner 2026-10-27: casting to a signed release in one go). What was agreed starts from the role and the project –
 * shoot day from Inspelningar, the limits on use the other actors have, the guardian from the library – then the
 * producer previews the release and has it signed on site now, or emails it.
 */
function BookDialog(p: {
  api: Api;
  id: string;
  role: Role;
  c: Candidate;
  shoots: ShootDay[];
  usage: string | null;
  onDone: (r: Role) => void;
  onClose: () => void;
}) {
  const lang = useLang();
  const t = TX[lang];
  const [fee, setFee] = useState(p.role.fee === null ? "" : String(p.role.fee));
  const [date, setDate] = useState(p.role.shootDate ?? firstShoot(p.shoots));
  const [usage, setUsage] = useState(p.usage ?? "");
  const [minor, setMinor] = useState(Boolean(p.c.guardianName));
  const [gName, setGName] = useState(p.c.guardianName ?? "");
  const [gEmail, setGEmail] = useState(p.c.guardianEmail ?? "");
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [castingId, setCastingId] = useState<string | null>(null);
  const [sent, setSent] = useState<{ to: string; url: string | null } | null>(
    null,
  );
  const byBirth = p.c.birthDate
    ? minorOn(p.c.birthDate, date || todayKey())
    : null;
  const child = byBirth ?? minor;
  const ready = !child || gName.trim().length > 0;
  const signer = child ? gName.trim() || t.theGuardian : p.c.name;
  const base = (cid: string) => `/api/projects/${p.id}/actors/${cid}/signing`;
  const book = async () => {
    setBusy("book");
    setErr(null);
    try {
      const r = await p.api.post<BookResult>(
        `/api/projects/${p.id}/casting/candidates/${p.c.id}/book`,
        {
          fee: fee || null,
          shootDate: date || null,
          usage: usage.trim() || null,
          ...(byBirth === null ? { minor } : {}),
          ...(child
            ? { guardianName: gName, guardianEmail: gEmail || null }
            : {}),
        },
      );
      setCastingId(r.castingId);
      p.onDone(r.role);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(null);
    }
  };
  const sign = async (onsite: boolean) => {
    if (!castingId) return;
    setBusy(onsite ? "onsite" : "send");
    setErr(null);
    try {
      const r = await p.api.post<SendOut>(
        base(castingId),
        onsite ? { onsite: true } : {},
      );
      if (r.onsite) {
        // this device is the signer's for now: the page opens without the code (the log says it was on site)
        try {
          sessionStorage.setItem(
            `kff.esign.${r.onsite.token.slice(0, 36)}`,
            r.onsite.session,
          );
        } catch {
          /* private mode: the page asks for the code instead */
        }
        location.assign(`/esign/${r.onsite.token}`);
        return;
      }
      const to =
        r.request?.parties.find((x) => x.role === "signer")?.email ?? signer;
      setSent({ to, url: r.links[0]?.url ?? null });
      p.onDone((await p.api.get<CastingView>(`/api/projects/${p.id}/casting`)).roles.find((x) => x.id === p.role.id) ?? p.role);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(null);
    }
  };
  if (sent)
    return (
      <Dialog
        title={t.bookedTitle(p.c.name)}
        onClose={p.onClose}
        foot={
          <button type="button" class="btn btn-primary" onClick={p.onClose}>
            {t.done}
          </button>
        }
      >
        <div class="stack">
          {sent.url ? (
            <>
              <Callout tone="info" title={t.sendRelease}>
                {t.linkBody(sent.to)}
              </Callout>
              <div class="cluster">
                <button
                  type="button"
                  class="btn btn-primary"
                  onClick={() => void copy(sent.url!, t.copied, t.copyFailed)}
                >
                  {t.copyLink}
                </button>
              </div>
            </>
          ) : (
            <Callout tone="ok" title={t.sendRelease}>
              {t.mailedTo(sent.to)}
            </Callout>
          )}
        </div>
      </Dialog>
    );
  if (castingId)
    return (
      <Dialog
        title={t.bookedTitle(p.c.name)}
        onClose={p.onClose}
        foot={
          <button type="button" class="btn" onClick={p.onClose}>
            {t.later}
          </button>
        }
      >
        <div class="stack">
          <p>{t.howSign(signer)}</p>
          <div class="cast-sign-choice">
            <button
              type="button"
              class="btn btn-primary"
              disabled={busy !== null}
              onClick={() => void sign(true)}
            >
              {busy === "onsite" ? t.opening : t.signOnsite}
            </button>
            <span class="small muted">{t.signOnsiteHint(signer)}</span>
            <button
              type="button"
              class="btn"
              disabled={busy !== null}
              onClick={() => void sign(false)}
            >
              {busy === "send" ? t.sending : t.sendSigning}
            </button>
            <span class="small muted">{t.sendSigningHint(signer)}</span>
          </div>
          <p class="small">
            <a
              href={`/projects/${p.id}/actors/${castingId}/release`}
              target="_blank"
              rel="noopener"
            >
              {t.preview}
            </a>
          </p>
          {err ? <ErrorBox error={err} /> : null}
        </div>
      </Dialog>
    );
  return (
    <Dialog
      title={t.bookTitle(p.c.name, p.role.name)}
      onClose={p.onClose}
      onSubmit={() => void book()}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            disabled={busy !== null || !ready}
          >
            {busy ? t.booking : t.bookSend}
          </button>
        </>
      }
    >
      <div class="stack tal-form">
        <p>{t.bookBody}</p>
        <div class="act-grid">
          <label class="field">
            <span class="label">{t.agreedFee}</span>
            <span class="hint">{t.feeEmpty}</span>
            <input
              class="input"
              inputMode="numeric"
              value={fee}
              onInput={(e) => setFee(val(e))}
            />
          </label>
          <ShootDateField
            label={t.shootDate}
            value={date}
            onChange={setDate}
            shoots={p.shoots}
          />
        </div>
        <label class="field">
          <span class="label">{t.usage}</span>
          <span class="hint">{p.usage ? t.usageFromProject : t.usageHint}</span>
          <textarea
            class="textarea act-usage"
            value={usage}
            maxLength={1000}
            onInput={(e) => setUsage(val(e))}
          />
        </label>
        {byBirth !== null ? (
          <p class="small muted">
            {t.under18} {byBirth ? t.byBirthYes : t.byBirthNo}
          </p>
        ) : (
          <label class="check">
            <input
              type="checkbox"
              checked={minor}
              onChange={() => setMinor(!minor)}
            />
            <span>{t.guardianOption}</span>
          </label>
        )}
        {child ? (
          <>
            <p class="small muted">{t.guardianHint}</p>
            <div class="act-grid">
              <label class="field">
                <span class="label">{t.guardianName}</span>
                <input
                  class="input"
                  value={gName}
                  onInput={(e) => setGName(val(e))}
                />
              </label>
              <label class="field">
                <span class="label">{t.guardianEmail}</span>
                <input
                  class="input"
                  type="email"
                  value={gEmail}
                  onInput={(e) => setGEmail(val(e))}
                />
              </label>
            </div>
          </>
        ) : null}
        {err ? <ErrorBox error={err} /> : null}
      </div>
    </Dialog>
  );
}

/* ---------------- the Casting block on the project's Actors tab ---------------- */

function CandidateRow(p: {
  c: Candidate;
  picked: boolean;
  onPick: () => void;
  busy: boolean;
  act: (c: Candidate, what: "book" | "ask" | Stage | "remove") => void;
  booked: BookedSlot | null;
}) {
  const lang = useLang();
  const t = TX[lang];
  const { c } = p;
  // booked: the row is the release – its status and next step (sign on site, signing…, the PDF)
  if (c.stage === "booked" && p.booked)
    return (
      <li class="act-row cast-row">
        <span class="cast-pick" aria-hidden="true" />
        <div class="act-main">
          <div>
            <strong>{c.name}</strong> <Pill tone={p.booked.tone}>{p.booked.status}</Pill>
          </div>
          {p.booked.main}
        </div>
        <div class="cluster act-actions">{p.booked.actions}</div>
      </li>
    );
  const pickable = c.stage !== "booked" && c.stage !== "passed";
  const items = [
    ...(c.stage !== "yes" && pickable
      ? [{ label: t.markYes, run: () => p.act(c, "yes") }]
      : []),
    ...(c.stage !== "no" && pickable
      ? [{ label: t.markNo, run: () => p.act(c, "no") }]
      : []),
    ...(pickable
      ? [{ label: t.markPassed, run: () => p.act(c, "passed") }]
      : []),
    ...(c.stage === "passed"
      ? [{ label: t.backToCandidate, run: () => p.act(c, "shortlist") }]
      : []),
    ...(c.stage !== "booked"
      ? [
          {
            label: t.removeCandidate,
            run: () => p.act(c, "remove"),
            danger: true,
          },
        ]
      : []),
  ];
  return (
    <li class="act-row cast-row">
      <label class="check cast-pick">
        <input
          type="checkbox"
          disabled={!pickable}
          checked={p.picked}
          onChange={p.onPick}
          aria-label={c.name}
        />
      </label>
      <div class="act-main">
        <div>
          <strong>{c.name}</strong>{" "}
          {c.stage === "booked" && c.release ? (
            <Pill tone={c.release === "signed" ? "ok" : "info"}>
              {t.release[c.release]}
            </Pill>
          ) : (
            <Pill tone={TONE[c.stage]}>{t.stage[c.stage]}</Pill>
          )}
        </div>
        <div class="muted small">
          {talentLine(c, lang) || (c.email ?? c.phone ?? "")}
        </div>
        {c.askedAt ? (
          <div class="small">
            {t.askedOn(when(c.askedAt, lang))}
            {c.answeredAt ? ` · ${t.answered(when(c.answeredAt, lang))}` : ""}
          </div>
        ) : null}
        {c.answerNote ? (
          <div class="small cast-note">“{c.answerNote}”</div>
        ) : null}
        {c.note ? <div class="small muted">{c.note}</div> : null}
        {!c.email && pickable ? (
          <div class="small muted">{t.noEmail}</div>
        ) : null}
      </div>
      <div class="cluster act-actions">
        {c.tapeUrl ? (
          <a
            class="btn btn-sm"
            href={c.tapeUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t.selfTape}
          </a>
        ) : null}
        {c.stage === "shortlist" && (c.email || c.phone) ? (
          <button
            type="button"
            class="btn btn-sm btn-primary"
            disabled={p.busy}
            onClick={() => p.act(c, "ask")}
          >
            {t.askOne}
          </button>
        ) : null}
        {pickable && c.stage !== "no" ? (
          <button
            type="button"
            class={`btn btn-sm${c.stage === "yes" ? " btn-primary" : ""}`}
            disabled={p.busy}
            onClick={() => p.act(c, "book")}
          >
            {t.book}
          </button>
        ) : null}
        <RowMenu label={t.rowMenu(c.name)} items={items} />
      </div>
    </li>
  );
}

export function ProjectCasting(p: {
  api: Api;
  id: string;
  onBooked: () => void;
  /** the Actors tab draws a booked person's release on their role */
  booked?: (castingId: string) => BookedSlot | null;
  /** the releases shown on a role, so the Actors tab lists only the rest */
  onCastings?: (ids: string[]) => void;
  onAddDirect?: () => void;
  signed?: number;
  total?: number;
}) {
  const lang = useLang();
  const t = TX[lang];
  const [v, setV] = useState<CastingView | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [picked, setPicked] = useState<Record<string, string[]>>({});
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState<
    | { kind: "role"; edit: Role | null }
    | { kind: "add" | "remove"; role: Role }
    | { kind: "ask"; role: Role; who: Candidate[] }
    | { kind: "book"; role: Role; c: Candidate }
    | null
  >(null);
  const load = () =>
    p.api.get<CastingView>(`/api/projects/${p.id}/casting`).then(setV, setErr);
  useEffect(() => {
    void load();
  }, [p.id]);
  useEffect(() => {
    if (v)
      p.onCastings?.(
        v.roles.flatMap((r) =>
          r.candidates.flatMap((c) => (c.castingId ? [c.castingId] : [])),
        ),
      );
  }, [v]);
  // a link from the project's Activity tab opens on its role
  useHashFocus(Boolean(v));
  if (err) return <ErrorBox error={err} />;
  if (!v) return <Loading />;
  const put = (r: Role) =>
    setV({
      ...v,
      roles: v.roles.some((x) => x.id === r.id)
        ? v.roles.map((x) => (x.id === r.id ? r : x))
        : [...v.roles, r],
    });
  const act = async (c: Candidate, what: "book" | "ask" | Stage | "remove") => {
    if (what === "ask") {
      setDialog({
        kind: "ask",
        role: v.roles.find((r) => r.id === c.roleId)!,
        who: [c],
      });
      return;
    }
    setBusy(true);
    try {
      if (what === "book") {
        setDialog({
          kind: "book",
          role: v.roles.find((r) => r.id === c.roleId)!,
          c,
        });
        return;
      } else if (what === "remove")
        put(
          (
            await p.api.del<{ role: Role }>(
              `/api/projects/${p.id}/casting/candidates/${c.id}`,
            )
          ).role,
        );
      else
        put(
          (
            await p.api.put<{ role: Role }>(
              `/api/projects/${p.id}/casting/candidates/${c.id}`,
              { stage: what },
            )
          ).role,
        );
      setPicked({
        ...picked,
        [c.roleId]: (picked[c.roleId] ?? []).filter((x) => x !== c.id),
      });
    } catch (e) {
      toast({ text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <section class="blk" aria-labelledby="cast-h">
      <div class="blk-head">
        <h2 class="blk-title" id="cast-h">
          {t.casting}{" "}
          {p.total ? (
            <Pill tone={p.signed === p.total ? "ok" : "neutral"}>
              {t.nSigned(p.signed ?? 0, p.total)}
            </Pill>
          ) : null}
        </h2>
        <div class="blk-actions">
          <button
            type="button"
            class="btn btn-sm btn-primary"
            onClick={() => setDialog({ kind: "role", edit: null })}
          >
            {t.newRole}
          </button>
          {p.onAddDirect ? (
            <button type="button" class="btn btn-sm" onClick={p.onAddDirect}>
              {t.addDirect}
            </button>
          ) : null}
        </div>
      </div>
      {v.roles.length ? <p class="blk-info">{t.castingInfo}</p> : null}
      {(() => {
        const w = shootWarning(v.roles, new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Stockholm" }));
        return w ? (
          <div class="callout callout--warn cast-warn" role="status">
            <strong>{t.shootWarn(day(w.date, lang), w.open, w.of)}</strong>
          </div>
        ) : null;
      })()}
      {v.roles.length ? (
        <div class="stack">
          {v.roles.map((r) => {
            const sel = picked[r.id] ?? [];
            const who = r.candidates.filter((c) => sel.includes(c.id));
            return (
              <div key={r.id} id={`role-${r.id}`} class="cast-role">
                <div class="blk-head">
                  <div>
                    <h3 class="cast-role-title">{r.name}</h3>
                    <div class="cast-progress">
                      <ProcessBar done={roleProgress(r)} label={t.progress[roleProgress(r)]!} />
                    </div>
                    <div class="small muted">
                      {[
                        roleLine(r, lang),
                        t.nCandidates(r.candidates.length, r.booked),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </div>
                    {r.brief ? <div class="small">{r.brief}</div> : null}
                  </div>
                  <div class="cluster">
                    <button
                      type="button"
                      class="btn btn-sm"
                      onClick={() => setDialog({ kind: "add", role: r })}
                    >
                      {t.addPeople}
                    </button>
                    {r.candidates.length ? (
                      <button
                        type="button"
                        class="btn btn-sm btn-primary"
                        disabled={!who.length}
                        title={who.length ? undefined : t.pickToAsk}
                        onClick={() => setDialog({ kind: "ask", role: r, who })}
                      >
                        {t.askPicked(who.length)}
                      </button>
                    ) : null}
                    <RowMenu
                      label={t.roleMenu(r.name)}
                      items={[
                        {
                          label: t.editRole,
                          run: () => setDialog({ kind: "role", edit: r }),
                        },
                        {
                          label: t.removeRole,
                          run: () => setDialog({ kind: "remove", role: r }),
                          danger: true,
                        },
                      ]}
                    />
                  </div>
                </div>
                {r.candidates.length ? (
                  <ul class="act-list">
                    {r.candidates.map((c) => (
                      <CandidateRow
                        key={c.id}
                        c={c}
                        busy={busy}
                        picked={sel.includes(c.id)}
                        onPick={() =>
                          setPicked({
                            ...picked,
                            [r.id]: sel.includes(c.id)
                              ? sel.filter((x) => x !== c.id)
                              : [...sel, c.id],
                          })
                        }
                        act={(c2, what) => void act(c2, what)}
                        booked={
                          c.castingId ? (p.booked?.(c.castingId) ?? null) : null
                        }
                      />
                    ))}
                  </ul>
                ) : (
                  <p class="small muted">{t.noCandidates}</p>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div class="cast-start">
          <ol class="cast-steps">
            {t.steps.map((x) => (
              <li key={x}>{x}</li>
            ))}
          </ol>
          <div class="cluster">
            <button
              type="button"
              class="btn btn-primary"
              onClick={() => setDialog({ kind: "role", edit: null })}
            >
              {t.firstRole}
            </button>
          </div>
          <p class="small muted">{t.noRoles}</p>
        </div>
      )}
      {dialog?.kind === "role" ? (
        <RoleDialog
          api={p.api}
          id={p.id}
          edit={dialog.edit}
          shoots={v.shoots ?? []}
          onClose={() => setDialog(null)}
          onDone={(r) => {
            put(r);
            setDialog(null);
          }}
        />
      ) : null}
      {dialog?.kind === "add" ? (
        <AddPeopleDialog
          api={p.api}
          id={p.id}
          role={dialog.role}
          onClose={() => setDialog(null)}
          onDone={(r) => {
            put(r);
            setDialog(null);
          }}
        />
      ) : null}
      {dialog?.kind === "ask" ? (
        <AskDialog
          api={p.api}
          id={p.id}
          role={dialog.role}
          who={dialog.who}
          mailer={v.mailer && dialog.who.some((c) => c.byEmail)}
          onClose={() => setDialog(null)}
          onDone={(r) => {
            put(r);
            setPicked({ ...picked, [r.id]: [] });
          }}
        />
      ) : null}
      {dialog?.kind === "book" ? (
        <BookDialog
          api={p.api}
          id={p.id}
          role={dialog.role}
          c={dialog.c}
          shoots={v.shoots ?? []}
          usage={v.usage ?? null}
          onClose={() => setDialog(null)}
          onDone={(r) => {
            put(r);
            p.onBooked();
          }}
        />
      ) : null}
      {dialog?.kind === "remove" ? (
        <ConfirmDialog
          title={t.removeRoleTitle(dialog.role.name)}
          body={t.removeRoleBody}
          confirmLabel={t.removeRole}
          danger
          onClose={() => setDialog(null)}
          onConfirm={async () => {
            await p.api.del(
              `/api/projects/${p.id}/casting/roles/${dialog.role.id}`,
            );
            void load();
          }}
        />
      ) : null}
    </section>
  );
}

/* ---------------- "Cast…" from the library ---------------- */

interface ProjectOption {
  id: string;
  code: string;
  name: string;
  client: { name: string };
  deliveryMonth: string;
  mine: boolean;
  status: string;
}

export function CastDialog(p: {
  api: Api;
  people: { id: string; name: string; kinds: Kind[] }[];
  onDone: () => void;
  onClose: () => void;
}) {
  const lang = useLang();
  const t = TX[lang];
  const [projects, setProjects] = useState<ProjectOption[] | null>(null);
  const [pid, setPid] = useState("");
  const [roles, setRoles] = useState<Role[] | null>(null);
  const [rid, setRid] = useState("");
  const kind: Kind = p.people.every(
    (x) => x.kinds.includes("voice") && !x.kinds.includes("actor"),
  )
    ? "voice"
    : "actor";
  const [f, setF] = useState<RoleForm>(roleForm(null, kind));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => {
    p.api
      .get<{ projects: ProjectOption[] }>("/api/projects")
      .then(
        (r) =>
          setProjects(
            r.projects.filter(
              (x) => x.status !== "closed" && x.status !== "cancelled",
            ),
          ),
        setErr,
      );
  }, []);
  useEffect(() => {
    setRoles(null);
    setRid("");
    if (pid)
      p.api.get<CastingView>(`/api/projects/${pid}/casting`).then((r) => {
        setRoles(r.roles);
        setRid(
          r.roles.find((x) => x.kind === kind)?.id ?? r.roles[0]?.id ?? "",
        );
      }, setErr);
  }, [pid]);
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await p.api.post<{
        added: number;
        already: string[];
        projectId: string;
        projectName: string;
        roleName: string;
      }>("/api/talent/cast", {
        projectId: pid,
        ...(rid ? { roleId: rid } : { role: roleBody(f) }),
        talentIds: p.people.map((x) => x.id),
      });
      toast({
        text: t.castDone(r.added, r.roleName, r.projectName),
        undoLabel: t.open,
        undo: () => navigate(`/projects/${r.projectId}/actors`),
      });
      p.onDone();
    } catch (e) {
      setErr(e);
      setBusy(false);
    }
  };
  const mine = (projects ?? []).filter((x) => x.mine);
  const others = (projects ?? []).filter((x) => !x.mine);
  const label = (x: ProjectOption) => `${x.client.name} – ${x.name}`;
  const ready = pid && roles && (rid || f.name.trim());
  return (
    <Dialog
      title={t.castTitle(p.people.length)}
      sub={p.people.map((x) => x.name).join(", ")}
      onClose={p.onClose}
      onSubmit={() => void save()}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button
            type="submit"
            class="btn btn-primary"
            disabled={busy || !ready}
          >
            {busy ? t.casting2 : t.cast}
          </button>
        </>
      }
    >
      <div class="stack tal-form">
        {!projects ? (
          <Loading />
        ) : (
          <label class="field">
            <span class="label">{t.project}</span>
            <select class="input" value={pid} onChange={(e) => setPid(val(e))}>
              <option value="">{t.pickProject}</option>
              {mine.length ? (
                <optgroup label={t.mine}>
                  {mine.map((x) => (
                    <option key={x.id} value={x.id}>
                      {label(x)}
                    </option>
                  ))}
                </optgroup>
              ) : null}
              <optgroup label={mine.length ? t.others : t.project}>
                {others.map((x) => (
                  <option key={x.id} value={x.id}>
                    {label(x)}
                  </option>
                ))}
              </optgroup>
            </select>
          </label>
        )}
        {pid && !roles ? <Loading label={t.loadingRoles} /> : null}
        {pid && roles ? (
          <>
            {roles.length ? (
              <label class="field">
                <span class="label">{t.role}</span>
                <select
                  class="input"
                  value={rid}
                  onChange={(e) => setRid(val(e))}
                >
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {`${r.name} (${kindLabels(lang)[r.kind]})`}
                    </option>
                  ))}
                  <option value="">{t.newRoleOpt}</option>
                </select>
              </label>
            ) : null}
            {!rid ? <RoleFields f={f} set={setF} more="casting-quick" /> : null}
          </>
        ) : null}
        {err ? <ErrorBox error={err} /> : null}
      </div>
    </Dialog>
  );
}

/** The bar under the library while people are picked: "3 picked · Cast… · Clear". */
export function CastBar(p: {
  n: number;
  onCast: () => void;
  onClear: () => void;
}) {
  const t = TX[useLang()];
  if (!p.n) return null;
  return (
    <div class="cast-bar" role="region" aria-label={t.picked(p.n)}>
      <span>{t.picked(p.n)}</span>
      <button type="button" class="btn btn-primary" onClick={p.onCast}>
        {t.cast1}
      </button>
      <button type="button" class="btn btn-ghost" onClick={p.onClear}>
        {t.clear}
      </button>
    </div>
  );
}

/* ---------------- the candidate's answer page (public, Swedish or English) ---------------- */

interface AnswerView {
  firstName: string | null;
  lang: Lang;
  role: {
    name: string;
    kind: Kind;
    brief: string;
    shootDate: string | null;
    fee: number | null;
    answerBy: string | null;
  };
  answer: {
    available: boolean;
    note: string | null;
    tapeUrl: string | null;
    at: string;
  } | null;
  open: boolean;
  decided: "booked" | "passed" | null;
}

const ANSWER = texts(
  {
    docTitle: "Casting request · Klara Färdiga Film",
    fetching: "Fetching the request…",
    badLink: "The link does not work",
    loadFailed: "Couldn't load the request. Try again in a moment.",
    errors: {
      link_invalid:
        "The link no longer works. Contact your contact at Klara Färdiga Film.",
      link_expired:
        "The link has expired. Contact your contact at Klara Färdiga Film.",
      closed:
        "This role has already been filled. Thank you for wanting to take part!",
      "required:available": "Answer whether you can or not.",
      "bad_url:tapeUrl":
        "The link to your self-tape should start with https://.",
    } as Record<string, string>,
    hi: (n: string | null) => `Hi${n ? ` ${n}` : ""}!`,
    intro: (voice: boolean) =>
      `We at Klara Färdiga Film are casting for a ${voice ? "voice-over recording" : "shoot"} and think you could be a good fit.`,
    role: "Role",
    type: { actor: "Acting", voice: "Voice-over" } as Record<Kind, string>,
    shoot: "Shoot",
    fee: "Fee",
    answerBy: "Answer by",
    kr: (n: number) => `${n.toLocaleString("sv-SE")} kr excl. VAT`,
    can: "Can you take it?",
    yes: "Yes, I can",
    no: "No, I can't",
    note: "Anything we should know? (optional)",
    noteHint: "e.g. times you can, or other dates",
    tape: "Link to a self-tape (optional)",
    tapeHint:
      "YouTube, Vimeo, Google Drive, WeTransfer… make sure we can open it",
    send: "Send answer",
    sending: "Sending…",
    sendFailed: "Couldn't send the answer. Try again.",
    thanksYes: "Thanks – we got your answer",
    thanksNo: "Thanks for letting us know",
    nothingBooked:
      "Nothing is booked yet – we get back to you when the role is decided.",
    change: "Change my answer",
    yourAnswer: (yes: boolean): string =>
      yes ? "You answered that you can." : "You answered that you can't.",
    bookedTitle: "You're booked!",
    bookedBody:
      "We get back to you with the details and the model release to sign.",
    passedTitle: "The role has been filled",
    passedBody:
      "Thank you for wanting to take part – we hope to work with you another time.",
  },
  {
    docTitle: "Förfrågan · Klara Färdiga Film",
    fetching: "Hämtar förfrågan…",
    badLink: "Länken fungerar inte",
    loadFailed: "Kunde inte hämta förfrågan. Försök igen om en stund.",
    errors: {
      link_invalid:
        "Länken fungerar inte längre. Hör av dig till din kontakt på Klara Färdiga Film.",
      link_expired:
        "Länken har gått ut. Hör av dig till din kontakt på Klara Färdiga Film.",
      closed:
        "Den här rollen är redan tillsatt. Tack för att du ville vara med!",
      "required:available": "Svara om du kan eller inte.",
      "bad_url:tapeUrl": "Länken till din self-tape ska börja med https://.",
    },
    hi: (n: string | null) => `Hej${n ? ` ${n}` : ""}!`,
    intro: (voice: boolean) =>
      `Vi på Klara Färdiga Film castar till en ${voice ? "inspelning med voice-over" : "inspelning"} och tror att du kan passa.`,
    role: "Roll",
    type: { actor: "Skådespel", voice: "Voice-over" },
    shoot: "Inspelning",
    fee: "Arvode",
    answerBy: "Svara senast",
    kr: (n: number) => `${n.toLocaleString("sv-SE")} kr exkl. moms`,
    can: "Kan du ta den?",
    yes: "Ja, jag kan",
    no: "Nej, jag kan inte",
    note: "Något vi bör veta? (valfritt)",
    noteHint: "t.ex. tider du kan, eller andra datum",
    tape: "Länk till en self-tape (valfritt)",
    tapeHint:
      "YouTube, Vimeo, Google Drive, WeTransfer… se till att vi kan öppna den",
    send: "Skicka svar",
    sending: "Skickar…",
    sendFailed: "Kunde inte skicka svaret. Försök igen.",
    thanksYes: "Tack – vi har fått ditt svar",
    thanksNo: "Tack för att du hörde av dig",
    nothingBooked: "Inget är bokat än – vi hör av oss när rollen är bestämd.",
    change: "Ändra mitt svar",
    yourAnswer: (yes: boolean): string =>
      yes ? "Du har svarat att du kan." : "Du har svarat att du inte kan.",
    bookedTitle: "Du är bokad!",
    bookedBody:
      "Vi hör av oss med detaljerna och modellavtalet att signera.",
    passedTitle: "Rollen är tillsatt",
    passedBody:
      "Tack för att du ville vara med – vi hoppas på att jobba ihop en annan gång.",
  },
);

export function CastingAnswerPage(p: { token: string }) {
  const api = createApi(null);
  const [lang, flipLang] = usePublicLang();
  const t = ANSWER[lang];
  const [v, setV] = useState<AnswerView | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [can, setCan] = useState<boolean | null>(null);
  const [note, setNote] = useState("");
  const [tape, setTape] = useState("");
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formErr, setFormErr] = useState<unknown>(null);
  useEffect(() => {
    document.title = t.docTitle;
  }, [lang]);
  useEffect(() => {
    api
      .get<AnswerView>(`/api/casting-answer/${encodeURIComponent(p.token)}`)
      .then((x) => {
        setV(x);
        if (x.answer) {
          setCan(x.answer.available);
          setNote(x.answer.note ?? "");
          setTape(x.answer.tapeUrl ?? "");
        }
      }, setErr);
  }, [p.token]);
  const send = async () => {
    setBusy(true);
    setFormErr(null);
    try {
      const x = await api.post<AnswerView>(
        `/api/casting-answer/${encodeURIComponent(p.token)}`,
        { available: can, note, tapeUrl: can ? tape : "" },
      );
      setV(x);
      setEditing(false);
      scrollTo(0, 0);
    } catch (e) {
      setFormErr(e);
    } finally {
      setBusy(false);
    }
  };
  const fmt = (k: string) => (lang === "sv" ? dmSv(k) : dm(k));
  return (
    <PublicFrame lang={lang} onLang={flipLang}>
      {err ? (
        <section class="portal-card">
          <h1 class="portal-h1">{t.badLink}</h1>
          <p>{publicError(err, t.errors, t.loadFailed)}</p>
        </section>
      ) : !v ? (
        <Loading label={t.fetching} />
      ) : (
        <section class="portal-card stack apply-form">
          <h1 class="portal-h1">{t.hi(v.firstName)}</h1>
          <p>{t.intro(v.role.kind === "voice")}</p>
          <dl class="cast-facts">
            <dt>{t.role}</dt>
            <dd>
              <strong>{v.role.name}</strong> · {t.type[v.role.kind]}
            </dd>
            {v.role.brief ? (
              <>
                <dt class="sr-only">{t.role}</dt>
                <dd class="cast-brief">{v.role.brief}</dd>
              </>
            ) : null}
            {v.role.shootDate ? (
              <>
                <dt>{t.shoot}</dt>
                <dd>{fmt(v.role.shootDate)}</dd>
              </>
            ) : null}
            {v.role.fee ? (
              <>
                <dt>{t.fee}</dt>
                <dd>{t.kr(v.role.fee)}</dd>
              </>
            ) : null}
            {v.role.answerBy ? (
              <>
                <dt>{t.answerBy}</dt>
                <dd>{fmt(v.role.answerBy)}</dd>
              </>
            ) : null}
          </dl>
          {v.decided === "booked" ? (
            <Callout tone="ok" title={t.bookedTitle}>
              {t.bookedBody}
            </Callout>
          ) : v.decided === "passed" ? (
            <Callout tone="info" title={t.passedTitle}>
              {t.passedBody}
            </Callout>
          ) : v.answer && !editing ? (
            <>
              <Callout
                tone="ok"
                title={v.answer.available ? t.thanksYes : t.thanksNo}
              >
                {t.yourAnswer(v.answer.available)}{" "}
                {v.answer.available ? t.nothingBooked : null}
              </Callout>
              {v.open ? (
                <div>
                  <button
                    type="button"
                    class="btn"
                    onClick={() => setEditing(true)}
                  >
                    {t.change}
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <form
              class="stack"
              onSubmit={(e) => {
                e.preventDefault();
                void send();
              }}
            >
              <fieldset class="fieldset">
                <legend class="label">{t.can}</legend>
                <div class="cluster">
                  <label class="check">
                    <input
                      type="radio"
                      name="can"
                      checked={can === true}
                      onChange={() => setCan(true)}
                    />
                    <span>{t.yes}</span>
                  </label>
                  <label class="check">
                    <input
                      type="radio"
                      name="can"
                      checked={can === false}
                      onChange={() => setCan(false)}
                    />
                    <span>{t.no}</span>
                  </label>
                </div>
              </fieldset>
              <label class="field">
                <span class="label">{t.note}</span>
                <span class="hint">{t.noteHint}</span>
                <textarea
                  class="textarea"
                  value={note}
                  maxLength={1000}
                  onInput={(e) => setNote(val(e))}
                />
              </label>
              {can ? (
                <label class="field">
                  <span class="label">{t.tape}</span>
                  <span class="hint">{t.tapeHint}</span>
                  <input
                    class="input"
                    type="url"
                    inputMode="url"
                    placeholder="https://"
                    value={tape}
                    onInput={(e) => setTape(val(e))}
                  />
                </label>
              ) : null}
              {formErr ? (
                <p class="field-error" role="alert">
                  {publicError(formErr, t.errors, t.sendFailed)}
                </p>
              ) : null}
              <div>
                <button
                  type="submit"
                  class="btn btn-primary"
                  disabled={busy || can === null}
                >
                  {busy ? t.sending : t.send}
                </button>
              </div>
            </form>
          )}
        </section>
      )}
    </PublicFrame>
  );
}
